/**
 * Layout 렌더링 유틸리티
 * 건축 요소 및 UI 렌더링 함수
 */

import React from 'react';
import type { ArchitectureElement } from '../models/Layout';

// 미리 정의된 구역 색상 팔레트
export const ZONE_COLORS = [
  { name: '파란색', value: '#3B82F6' },
  { name: '초록색', value: '#10B981' },
  { name: '빨간색', value: '#EF4444' },
  { name: '보라색', value: '#8B5CF6' },
  { name: '노란색', value: '#F59E0B' },
  { name: '분홍색', value: '#EC4899' },
  { name: '청록색', value: '#06B6D4' },
  { name: '주황색', value: '#F97316' },
  { name: '회색', value: '#6B7280' },
  { name: '갈색', value: '#92400E' },
];

// 그리드 셀 크기 (픽셀)
export const CELL_SIZE = 80;

/**
 * 건축 요소 렌더링 함수
 */
export const renderArchitectureElement = (element: ArchitectureElement): JSX.Element => {
  switch (element.element_type) {
    case 'entrance':
      return (
        <div
          className="w-full h-full bg-white border-x-2 border-gray-400"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div
            className="w-full h-full bg-yellow-200 flex items-center justify-center"
            style={{
              background: 'linear-gradient(to bottom, #FEF3C7, #FDE68A)',
            }}
          >
            <span className="text-xs font-medium text-gray-700 drop-shadow-sm">출입구</span>
          </div>
        </div>
      );

    case 'entrance_with_gate':
      return (
        <div
          className="w-full h-full bg-white border-x-2 border-gray-400 relative"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div
            className="w-full h-full bg-yellow-200 flex flex-col items-center justify-center"
            style={{
              background: 'linear-gradient(to bottom, #FEF3C7, #FDE68A)',
            }}
          >
            <span className="text-xs font-medium text-gray-700 drop-shadow-sm">출입구</span>
            <div className="w-6 h-4 bg-blue-600 rounded-sm mt-1 flex items-center justify-center">
              <span className="text-xs text-white font-bold">G</span>
            </div>
          </div>
          {/* 게이트 표시선 */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-600 opacity-50"></div>
        </div>
      );

    case 'emergency_exit':
      return (
        <div
          className="w-full h-full bg-white border-x-2 border-gray-400"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div
            className="w-full h-full bg-green-200 flex items-center justify-center"
            style={{
              background: 'linear-gradient(to bottom, #D1FAE5, #A7F3D0)',
            }}
          >
            <span className="text-xs font-medium text-gray-700 drop-shadow-sm">비상구</span>
          </div>
        </div>
      );

    case 'window':
      return (
        <div
          className="w-full h-full bg-sky-100 border-y-2 border-gray-400"
          style={{
            background:
              element.wall_position === 'left'
                ? 'linear-gradient(to right, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))'
                : element.wall_position === 'right'
                  ? 'linear-gradient(to left, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))'
                  : element.wall_position === 'top'
                    ? 'linear-gradient(to bottom, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))'
                    : 'linear-gradient(to top, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))',
            boxShadow: 'inset 0 0 8px rgba(0,0,0,0.1)',
          }}
        >
          <div className="w-full h-full flex items-center justify-center relative overflow-hidden">
            {/* 창틀 효과 */}
            {(element.wall_position === 'left' || element.wall_position === 'right') && (
              <>
                <div className="absolute top-0 w-full h-0.5 bg-gray-500"></div>
                <div className="absolute bottom-0 w-full h-0.5 bg-gray-500"></div>
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-0.5 bg-gray-400"></div>
              </>
            )}
            {(element.wall_position === 'top' || element.wall_position === 'bottom') && (
              <>
                <div className="absolute left-0 h-full w-0.5 bg-gray-500"></div>
                <div className="absolute right-0 h-full w-0.5 bg-gray-500"></div>
                <div className="absolute left-1/2 -translate-x-1/2 h-full w-0.5 bg-gray-400"></div>
              </>
            )}
            <span className="text-xs font-medium text-gray-600 drop-shadow-sm z-10 bg-white/70 px-1 rounded">
              창문
            </span>
          </div>
        </div>
      );

    case 'pillar':
      return (
        <div
          className="w-full h-full bg-gray-700 rounded-lg flex items-center justify-center"
          style={{
            boxShadow: '0 8px 16px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)',
            background: 'linear-gradient(135deg, #4B5563, #374151)',
          }}
        >
          <div className="w-full h-full rounded-lg relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-600 to-gray-800 opacity-50"></div>
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-r from-white/20 to-transparent"></div>
            <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-l from-black/20 to-transparent"></div>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-300 drop-shadow-lg">
              기둥
            </span>
          </div>
        </div>
      );

    default:
      return <div className="w-full h-full bg-gray-200"></div>;
  }
};
